import { useState, type FormEvent } from "react";
import { TERMS_VERSION, type Me } from "@ai-rpg/protocol";
import { agree, signOut } from "../api.ts";
import { TermsText } from "./Terms.tsx";

const problemOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

interface Props {
  readonly name: string;
  readonly onAgreed: (me: Me) => void;
  readonly onSignedOut: () => void;
}

/**
 * What an account sees before anything else until its person has said they are 18 or older (and, where there
 * are terms of use in force, that they take them): one question, and the way out.
 */
export function Agree({ name, onAgreed, onSignedOut }: Props) {
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || (!adult && TERMS_VERSION === null)) return;
    setBusy(true);
    setProblem(null);
    try {
      onAgreed(await agree(TERMS_VERSION));
    } catch (err) {
      setProblem(problemOf(err));
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <p className="signin-place">The Table</p>
      <h1 className="lobby-title">Before you go on</h1>
      <p className="lobby-lede">
        Signed in as {name}. This table is for adults: its stories are told by an AI, and playing here is for people of
        18 and over.
      </p>
      {TERMS_VERSION !== null && (
        <div className="terms-frame" tabIndex={0}>
          <TermsText />
        </div>
      )}
      <form className="form" onSubmit={submit}>
        {TERMS_VERSION === null ? (
          <>
            <label className="agree-tick">
              <input type="checkbox" required checked={adult} onChange={(e) => setAdult(e.target.checked)} />
              <span>I am 18 or older.</span>
            </label>
            <button className="button button-primary" type="submit" disabled={busy || !adult}>
              {busy ? "Going on…" : "Go on"}
            </button>
          </>
        ) : (
          // One button for the terms, which say that whoever agrees is 18 or older.
          <button className="button button-primary" type="submit" disabled={busy}>
            {busy ? "Agreeing…" : "I agree"}
          </button>
        )}
        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}
      </form>
      <button
        className="link"
        onClick={() => {
          void signOut().then(onSignedOut);
        }}
      >
        Sign out
      </button>
    </main>
  );
}
